import { RefObject, useEffect, useState } from 'react';
import React from 'react';
import ReactQuill, { Quill } from 'react-quill';
import Card from 'ui/views/cards/Card';
import { KpiEmbed } from 'domain/kpis/types';
import { quarterSpacing } from 'ui/theme/themeConstants';
import KPIGraph from 'domain/kpis/KPIGraph';
import { companyUrls } from 'urls';
import { Link } from 'react-router-dom';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import IconButton from 'ui/elements/icons/IconButton';
import EditIcon from 'ui/elements/icons/EditIcon';

export const KPI_BLOT_CLASS = 'ql-kpi-embed';

export function stripKpiEmbeds(rawHtml: string) {
  const container = document.createElement('div');
  container.innerHTML = rawHtml;
  container.querySelectorAll('.' + KPI_BLOT_CLASS).forEach(blot => {
    blot.innerHTML = '';
  });
  return container.innerHTML;
}

// Quill blot definition
const BlockEmbed = Quill.import('blots/block/embed');
export interface EmbeddedKpiBlotValue {
  embed: KpiEmbed;
}

export class KpiBlot extends BlockEmbed {
  static blotName = 'kpiBlot';
  static tagName = 'div';
  static className = KPI_BLOT_CLASS;

  static create(value: EmbeddedKpiBlotValue) {
    const node = super.create() as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    // Store full embed data as JSON in data attribute
    node.dataset.embed = JSON.stringify(value.embed);
    // Add tabindex to make it focusable/selectable
    node.setAttribute('tabindex', '0');
    // Add cursor style to indicate it's interactive
    node.style.position = 'relative';

    return node;
  }

  static value(node: HTMLElement) {
    return {
      embed: node.dataset.embed ? JSON.parse(node.dataset.embed) : null,
    };
  }

  format(name: string, value: EmbeddedKpiBlotValue) {
    if (name === 'embed' && value) {
      this.domNode.dataset.embed = JSON.stringify(value.embed);
      // Mutation observer will automatically detect this attribute change
    }
  }
}
Quill.register('formats/kpiBlot', KpiBlot);

// Hook to collect blot nodes for portal mounting
interface MountPoint {
  node: HTMLElement;
  embed: KpiEmbed;
}

export function useHydrateKpiBlots(ref: RefObject<ReactQuill> | RefObject<HTMLElement>) {
  const [mounts, setMounts] = useState<MountPoint[]>([]);

  useEffect(() => {
    const current = ref.current;
    if (!current) return;

    const root: HTMLElement =
      'getEditor' in current ? (current.getEditor().root as HTMLElement) : (current as HTMLElement);

    const scan = () => {
      const nodes = Array.from(root.querySelectorAll(`.${KPI_BLOT_CLASS}`)) as HTMLElement[];

      setMounts(
        nodes
          .filter(n => n.dataset.embed)
          .map(n => {
            try {
              return {
                node: n,
                embed: JSON.parse(n.dataset.embed!) as KpiEmbed,
              };
            } catch (e) {
              return null;
            }
          })
          .filter((m): m is MountPoint => m !== null),
      );
    };

    scan();
    const observer = new MutationObserver(scan);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true, // Watch for attribute changes
      attributeFilter: ['data-embed'], // Only watch embed changes
    });
    return () => observer.disconnect();
  }, [ref]);

  return mounts;
}

// Embed component - fetches KPI data and renders the graph
interface Props {
  embed: KpiEmbed;
  isEditable?: boolean;
}

export function KpiEmbedCard({ embed, isEditable = false }: Props) {
  const [isHovered, setIsHovered] = useState(false);

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Find the blot DOM node (the parent with KPI_BLOT_CLASS)
    const blotNode = (e.currentTarget as HTMLElement).closest(`.${KPI_BLOT_CLASS}`) as HTMLElement;
    // Dispatch the same custom event that the blot expects
    const event = new CustomEvent('kpi-embed-click', {
      bubbles: true,
      detail: { embed, node: blotNode },
    });
    blotNode?.dispatchEvent(event);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Find the blot DOM node and remove it
    const blotNode = (e.currentTarget as HTMLElement).closest(`.${KPI_BLOT_CLASS}`) as HTMLElement;
    if (blotNode) {
      const blot = Quill.find(blotNode);
      if (blot) {
        blot.remove();
      }
    }
  };

  // Check if we have embedded values
  if (!embed.values || embed.values.length === 0) {
    return (
      <Card className="u-content-spacing-bottom">
        <div className="text-grey text-italic u-content-padding">This KPI has no data available</div>
      </Card>
    );
  }

  // Use all embedded values - KPIGraph will handle filtering based on dataPointCount
  // For bar charts with comparison, this includes both current and previous year data
  const displayValues = embed.values;

  return (
    <div
      onMouseEnter={() => isEditable && setIsHovered(true)}
      onMouseLeave={() => isEditable && setIsHovered(false)}
      style={{ position: 'relative' }}
    >
      {isEditable && isHovered && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            display: 'flex',
            gap: quarterSpacing,
          }}
        >
          <IconButton color="indigo" onClick={handleEditClick}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton color="indigo" onClick={handleDeleteClick}>
            <TrashIcon fontSize="small" />
          </IconButton>
        </div>
      )}
      <Card color="light-grey" hover={isEditable ? 'drop-shadow' : undefined}>
        <div className="u-fade-in">
          <KPIGraph
            name={embed.metadata.kpiName}
            unitOfMeasure={embed.metadata.unitOfMeasure}
            values={displayValues}
            displayType={embed.displayConfig.displayType}
            showXAxis={embed.displayConfig.showXAxis}
            showYAxis={embed.displayConfig.showYAxis}
            comparisonEnabled={embed.displayConfig.comparisonEnabled ?? false}
            comparisonPeriod={embed.displayConfig.comparisonPeriod ?? 'Month'}
            trendIndicator={embed.displayConfig.trendIndicator ?? 'Up'}
            dataPointCount={embed.displayConfig.dataPointCount}
          />
        </div>
        {!isEditable && embed.metadata.companySlug && (
          <Link className="text-link-small" to={companyUrls.overview(embed.metadata.companySlug, 'kpis')}>
            View KPI →
          </Link>
        )}
      </Card>
    </div>
  );
}
